<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useOptionsStore } from '@/stores/options';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { isRole } from '@/utils/roleHelper';

const { relationships, provinces } = storeToRefs(useOptionsStore());
const { profile } = storeToRefs(useUserStore());
const roles = computed(() => isRole(profile.value));

const props = defineProps({
    initialGuardians: {
        type: Array,
        default: () => []
    }
});

const guardians = ref([]);

onMounted(() => {
    guardians.value = props.initialGuardians;
});

const emit = defineEmits(['guardians-updated']);
watch(guardians, (newVal) => {
    emit('guardians-updated', newVal);
}, { deep: true });

const addGuardian = () => {
    guardians.value.push({
        first_name: '',
        last_name: '',
        relationship_id: null,
        province_id: null,
        email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        client_resides_with: false,
        is_active_guardian: true,
        relationship_other_description: null
    });
};

const removeGuardian = (index) => {
    guardians.value.splice(index, 1);
};

const allowEdit = computed(() => {
    if (roles.value.clientSupportWorker()) {
        return false;
    }
    return true;
});
</script>

<template>
<div>
    <div v-for="(guardian, index) in guardians" :key="index" class="border-b border-gray p-4 mb-4">
        <div class="md:grid lg:grid-cols-2 xl:grid-cols-3 md:gap-4">
            <StyledInput 
                v-model="guardian.first_name"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.first_name')"
                :name="`guardians.${index}.first_name`" />
            <StyledInput 
                v-model="guardian.last_name"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.last_name')"
                :name="`guardians.${index}.last_name`" />
            <StyledDropdown 
                v-model="guardian.relationship_id"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :options="relationships"
                :label="$t('label.relationship')"
                :name="`guardians.${index}.relationship_id`" />
            <StyledInput
                v-if="relationships.find(rel => rel.label == 'Other' && rel.value == guardian.relationship_id)"
                v-model="guardian.relationship_other_description"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.relationship_description')"
                :name="`guardians.${index}.relationship_description`" />
            <StyledDropdown 
                v-model="guardian.province_id"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :options="provinces"
                :label="$t('label.province')"
                :name="`guardians.${index}.province_id`"
                item-value="id"
                item-title="name" />
            <StyledInput 
                v-model="guardian.email"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.email')"
                :name="`guardians.${index}.email`" />
            <StyledInput 
                v-model="guardian.phone"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.phone')"
                :name="`guardians.${index}.phone`" />
            <StyledInput 
                v-model="guardian.address"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.address')"
                :name="`guardians.${index}.address`" />
            <StyledInput 
                v-model="guardian.city"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.city')"
                :name="`guardians.${index}.city`" />
            <StyledInput 
                v-model="guardian.postal_code"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.postal_code')"
                :name="`guardians.${index}.postal_code`" />
            <StyledCheckbox 
                v-model="guardian.client_resides_with"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.client_resides_with')"
                :name="`guardians.${index}.client_resides_with`" />
            <StyledCheckbox 
                v-model="guardian.is_active_guardian"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.is_active_guardian')"
                :name="`guardians.${index}.is_active_guardian`" />
            <StyledButton
                v-if="allowEdit"
                color="red" 
                size="small"    
                class="w-24 col-span-full"
                @click="removeGuardian(index)">
                {{ $t('label.remove') }}
            </StyledButton>
        </div>
    </div>
    <div v-if="allowEdit" class="bg-grey-200 my-4 pb-12 pt-12 px-12 border-black border border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-100" @click="addGuardian">
        <StyledButton class="flex items-center">
            <v-icon class="mr-2 text-white" icon="mdi-plus" />
            {{ $t('label.add_guardian') }}
        </StyledButton>
    </div>
</div>
</template>
